import { useTranslation } from 'react-i18next';

/*
 * The controls a tool's floating options are made of (ToolOptions,
 * ./WorkbenchContext.js; docs/ANALYSIS_WORKBENCH.md section 4b): sized for a
 * finger, each named, its value said beside it. The words are the caller's,
 * but for Magic fill's reach, which every mode with a Magic fill shares.
 *
 *   FloatRange   a slider, its label and its value (and unit) beside it
 *   FloatCheck   a checkbox and its label
 *   FloatChoice  one of a few, as radio buttons
 *   ReachSlider  Magic fill's reach (its tolerance): how alike a pixel must
 *                be to the one tapped to be filled. Moving it redoes the
 *                last fill, which grows or shrinks in place while the
 *                picture is watched; `coverage` says what is filled now.
 *                `label` is the mode's own word for it (its drawer's), so
 *                the two never differ
 */
export function FloatRange({ label, value, min, max, step = 1, unit = '', onChange, testId, help = null,
                             disabled = false }) {
  return (
    <label className="wb-float-field" data-tip={help || undefined}>
      <span>{label}</span>
      <input type="range" min={min} max={max} step={step} value={value} disabled={disabled} aria-label={label}
             aria-valuetext={unit ? `${value} ${unit}` : undefined} data-testid={testId}
             onChange={e => onChange(Number(e.target.value))} />
      <bdi dir="ltr" className="wb-float-value">{unit ? `${value} ${unit}` : value}</bdi>
    </label>
  );
}

export function FloatCheck({ label, checked, onChange, testId, disabled = false, help = null }) {
  return (
    <label className="wb-float-field" data-tip={help || undefined}>
      <input type="checkbox" checked={checked} disabled={disabled} data-testid={testId}
             onChange={e => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

export function FloatChoice({ label, options, value, onChange, testId }) {
  return (
    <div role="radiogroup" aria-label={label} className="wb-group" data-testid={testId}>
      {options.map(option => (
        <button key={option.value} type="button" role="radio" aria-checked={value === option.value} className="wb-btn"
                data-testid={testId ? `${testId}-${option.value}` : undefined} onClick={() => onChange(option.value)}>
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function ReachSlider({ value, min, max, onChange, testId, coverage = null, disabled = false, label = null }) {
  const { t } = useTranslation('workbench');
  return (
    <>
      <FloatRange label={label || t('float.reach')} help={t('float.reachHelp')} value={value} min={min} max={max}
                  onChange={onChange} testId={testId} disabled={disabled} />
      {coverage && (
        <span className="wb-float-note" role="status" aria-live="polite" data-testid={testId ? `${testId}-coverage` : undefined}>
          {coverage}
        </span>
      )}
    </>
  );
}
